import { Component, Input, forwardRef, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { I18nService } from '../../services/i18n.service';
import { TranslatePipe } from '../../pipes/translate.pipe';

interface VideoPlayerConfig {
  youtubeUrl: string;
  title?: string;
  description?: string;
  width?: string;
  height?: string;
  autoplay?: boolean;
  controls?: boolean;
  allowFullscreen?: boolean;
}

@Component({
  selector: 'app-form-video-player',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  template: `
    <div class="form-section video-player-section">
      <label *ngIf="label" [attr.for]="id" [class.required-field]="required">{{ label }}</label>

      <div class="video-container" [style.width]="containerWidth" [style.height]="containerHeight">
        <div *ngIf="embedUrl && isValidUrl; else errorTemplate" class="video-wrapper">
          <!-- 影片標題 -->
          <div *ngIf="videoTitle" class="video-title">
            <h4>{{ videoTitle }}</h4>
          </div>

          <!-- 影片描述 -->
          <div *ngIf="videoDescription" class="video-description">
            <p>{{ videoDescription }}</p>
          </div>

          <!-- YouTube 嵌入播放器 -->
          <iframe
            [src]="embedUrl"
            [style.width]="playerWidth"
            [style.height]="playerHeight"
            allowfullscreen
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            class="youtube-player">
          </iframe>
        </div>

        <!-- 錯誤顯示模板 -->
        <ng-template #errorTemplate>
          <div class="video-error" [style.width]="playerWidth" [style.height]="playerHeight">
            <div class="error-content">
              <i class="fas fa-exclamation-triangle"></i>
              <p *ngIf="!youtubeUrl">{{ 'form.video_player.set_youtube_url' | translate }}</p>
              <p *ngIf="youtubeUrl && !isValidUrl">{{ 'form.video_player.invalid_youtube_url' | translate }}</p>
              <small *ngIf="youtubeUrl && !isValidUrl">{{ 'form.video_player.supported_formats' | translate }}<br>
                https://www.youtube.com/watch?v=VIDEO_ID<br>
                https://youtu.be/VIDEO_ID
              </small>
            </div>
          </div>
        </ng-template>
      </div>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormVideoPlayerComponent),
      multi: true,
    },
  ],
  styleUrls: ['total-component.scss'],
  styles: [`
    .video-player-section {
      margin-bottom: 20px;
    }

    .video-container {
      max-width: 100%;
    }

    .video-wrapper {
      width: 100%;
    }

    .video-title h4 {
      margin: 0 0 10px 0;
      color: #333;
      font-size: 18px;
      font-weight: 500;
    }

    .video-description p {
      margin: 0 0 15px 0;
      color: #666;
      font-size: 14px;
      line-height: 1.4;
      white-space: pre-wrap;
    }

    .youtube-player {
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      width: 100%;
      height: 100%;
    }

    .video-error {
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f8f9fa;
      border: 2px dashed #dee2e6;
      border-radius: 8px;
      color: #6c757d;
      text-align: center;
    }

    .error-content i {
      font-size: 32px;
      margin-bottom: 10px;
      color: #ffc107;
    }

    .error-content p {
      margin: 5px 0;
      font-size: 14px;
    }

    .error-content small {
      font-size: 12px;
      color: #999;
      line-height: 1.3;
    }

    /* 響應式設計 */
    @media (max-width: 768px) {
      .video-container {
        width: 100% !important;
      }

      .youtube-player,
      .video-error {
        height: 200px !important;
      }
    }

    @media (max-width: 480px) {
      .youtube-player,
      .video-error {
        height: 180px !important;
      }

      .video-title h4 {
        font-size: 16px;
      }

      .video-description p {
        font-size: 13px;
      }
    }
  `]
})
export class FormVideoPlayerComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() label: string = '';
  @Input() id: string = 'video-player';
  @Input() name: string = 'video-player';
  @Input() required: boolean = false;

  // YouTube 相關設定
  @Input() youtubeUrl: string = '';
  @Input() videoTitle: string = '';
  @Input() videoDescription: string = '';
  @Input() videoWidth: string = '100%';
  @Input() videoHeight: string = '315px';
  @Input() videoAutoplay: boolean = false;
  @Input() videoControls: boolean = true;
  @Input() videoAllowFullscreen: boolean = true;

  value: VideoPlayerConfig = {
    youtubeUrl: '',
    title: '',
    description: '',
    width: '100%',
    height: '315px',
    autoplay: false,
    controls: true,
    allowFullscreen: true
  };

  embedUrl: SafeResourceUrl | null = null;
  isValidUrl: boolean = false;

  onChange = (value: any) => {};
  onTouched = () => {};

  constructor(private sanitizer: DomSanitizer, private i18nService: I18nService) {}

  ngOnInit(): void {
    // 設定預設 label（如果沒有提供）
    if (!this.label) {
      this.label = this.i18nService.translate('form.video_player.label');
    }
    this.updateVideoConfig();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['youtubeUrl'] || changes['videoAutoplay'] || changes['videoControls']) {
      this.updateVideoConfig();
    }
  }

  private updateVideoConfig(): void {
    this.value = {
      youtubeUrl: this.youtubeUrl,
      title: this.videoTitle,
      description: this.videoDescription,
      width: this.videoWidth,
      height: this.videoHeight,
      autoplay: this.videoAutoplay,
      controls: this.videoControls,
      allowFullscreen: this.videoAllowFullscreen
    };

    this.updateEmbedUrl();
    this.onChange(this.value);
  }

  private updateEmbedUrl(): void {
    if (!this.youtubeUrl) {
      this.embedUrl = null;
      this.isValidUrl = false;
      return;
    }

    const videoId = this.extractVideoId(this.youtubeUrl);
    if (videoId) {
      const params = new URLSearchParams();
      if (this.videoAutoplay) params.set('autoplay', '1');
      if (!this.videoControls) params.set('controls', '0');
      params.set('rel', '0'); // 不顯示相關影片
      params.set('modestbranding', '1'); // 隱藏 YouTube logo

      const paramString = params.toString();
      const embedUrlString = `https://www.youtube.com/embed/${videoId}${paramString ? '?' + paramString : ''}`;

      this.embedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(embedUrlString);
      this.isValidUrl = true;
    } else {
      this.embedUrl = null;
      this.isValidUrl = false;
    }
  }

  private extractVideoId(url: string): string | null {
    // 支援的 YouTube URL 格式
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/v\/([a-zA-Z0-9_-]{11})/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }

    return null;
  }

  get containerWidth(): string {
    return this.videoWidth || '100%';
  }

  get containerHeight(): string {
    return 'auto';
  }

  get playerWidth(): string {
    return '100%';
  }

  get playerHeight(): string {
    return this.videoHeight || '315px';
  }

  // ControlValueAccessor 實作
  writeValue(value: any): void {
    if (value && typeof value === 'object') {
      this.value = { ...this.value, ...value };
      this.youtubeUrl = value.youtubeUrl || '';
      this.videoTitle = value.title || '';
      this.videoDescription = value.description || '';
      this.videoWidth = value.width || '100%';
      this.videoHeight = value.height || '315px';
      this.videoAutoplay = value.autoplay || false;
      this.videoControls = value.controls !== false; // 預設為 true
      this.videoAllowFullscreen = value.allowFullscreen !== false; // 預設為 true
      this.updateEmbedUrl();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // 影片播放器本身是唯讀的，不需要特別處理 disabled 狀態
  }
}
